import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const FIELD_GROUPS = [
  {
    title: 'Deceased',
    fields: [
      ['deceased_name', 'Name of deceased', 'text'],
      ['date_of_death', 'Date of death', 'text'],
      ['date_of_birth', 'Date of birth', 'text'],
      ['date_of_burial', 'Date of burial / interment', 'text'],
      ['deceased_address', "Deceased's last address", 'text'],
      ['relationship_to_deceased', 'Applicant relationship to deceased', 'text'],
    ],
  },
  {
    title: 'Churchyard / Diocese (for official forms)',
    fields: [
      ['parish', 'Parish', 'text'],
      ['archdeaconry', 'Archdeaconry', 'text'],
    ],
  },
  {
    title: 'Memorial Specification',
    fields: [
      ['memorial_description', 'Memorial description', 'text'],
      ['memorial_finish', 'Finish (e.g. Polished all over)', 'text'],
      ['fixing_method', 'Fixing method (e.g. NAMM ground anchor)', 'text'],
      ['headstone_height', 'Overall height', 'text'],
      ['headstone_width', 'Overall width', 'text'],
      ['headstone_thickness', 'Thickness', 'text'],
      ['base_width', 'Base width', 'text'],
      ['base_depth', 'Base depth', 'text'],
      ['base_thickness', 'Base thickness', 'text'],
    ],
  },
  {
    title: 'Registrations',
    fields: [
      ['bramm_number', 'BRAMM registration number', 'text'],
      ['namm_number', 'NAMM registration number', 'text'],
    ],
  },
];

export default function ApplicationDetailsDialog({ open, onOpenChange, orderId, application, draftData, templateMissing = [], selectedTemplate = null, onSaved }) {
  const { token, API } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const [extra, setExtra] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const init = { ...(application?.extra_fields || {}) };
    // Prefill from smart defaults (inscription parsing / company registration) so
    // users only confirm rather than retype
    ['deceased_name', 'date_of_death', 'bramm_number', 'namm_number'].forEach((k) => {
      if (!init[k] && draftData?.[k]) init[k] = draftData[k];
    });
    setExtra(init);
  }, [open, application?.id, draftData?.deceased_name, draftData?.date_of_death, draftData?.bramm_number, draftData?.namm_number]);

  const set = (k, v) => setExtra((e) => ({ ...e, [k]: v }));
  const deedSame = extra.deed_holder_is_applicant !== false;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/orders/${orderId}/application`, { extra_fields: extra }, auth);
      toast.success('Application details saved');
      onSaved?.();
      onOpenChange(false);
    } catch { toast.error('Failed to save application details'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="application-details-dialog">
        <DialogHeader>
          <DialogTitle>Application Details</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Information already on the order (applicant, inscription, stone, plot) is pulled in automatically and stays
            live until the application is finalised. Only enter what's missing below.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md bg-slate-50 border p-3 text-xs text-slate-600 space-y-1">
          <p className="font-semibold text-slate-700 uppercase tracking-wide">From the order (live)</p>
          <p><span className="text-slate-400">Applicant:</span> {draftData?.applicant_name || '—'} · {draftData?.applicant_phone || 'no phone'} · {draftData?.applicant_email || 'no email'}</p>
          <p><span className="text-slate-400">Stone:</span> {[draftData?.stone_shape, draftData?.stone_material, draftData?.stone_colour].filter(Boolean).join(' · ') || '—'}</p>
          <p><span className="text-slate-400">Plot:</span> {draftData?.plot_number || 'not set'} · <span className="text-slate-400">Ornamentation:</span> {draftData?.ornamentation || 'none'}</p>
          <p className="whitespace-pre-line"><span className="text-slate-400">Inscription:</span> {draftData?.inscription ? draftData.inscription.slice(0, 200) : 'not set'}</p>
        </div>

        {selectedTemplate && templateMissing.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="template-missing-panel">
            <p className="text-sm font-semibold text-amber-800">
              Required for “{selectedTemplate.name}” — please complete
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {templateMissing.map((m) => (
                <div key={m.id || m.save_key}>
                  <Label className="text-xs">{m.label}</Label>
                  {m.type === 'textarea' ? (
                    <Textarea rows={2} value={extra[m.save_key] ?? ''} onChange={(e) => set(m.save_key, e.target.value)} data-testid={`missing-field-${m.save_key}`} />
                  ) : (
                    <Input value={extra[m.save_key] ?? ''} onChange={(e) => set(m.save_key, e.target.value)} data-testid={`missing-field-${m.save_key}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Memorial type for this application — pulled from the order, override if needed */}
        <div className="flex items-center gap-3">
          <div>
            <Label className="text-xs">Memorial type for application</Label>
            <Select
              value={extra.application_memorial_type || 'auto'}
              onValueChange={(v) => set('application_memorial_type', v === 'auto' ? null : v)}
            >
              <SelectTrigger className="w-64" data-testid="app-memorial-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto — from order ({draftData?.memorial_category_label || 'Full size memorial'})</SelectItem>
                <SelectItem value="full">Full size memorial</SelectItem>
                <SelectItem value="cremation">Cremation memorial</SelectItem>
                <SelectItem value="add_on">Additional inscription</SelectItem>
                <SelectItem value="children">Children's memorial</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {FIELD_GROUPS.map((g) => (
          <div key={g.title} className="space-y-2">
            <p className="text-sm font-semibold text-slate-700">{g.title}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {g.fields.map(([k, label, type]) => (
                <div key={k}>
                  <Label className="text-xs">{label}</Label>
                  <Input type={type} value={extra[k] ?? ''} onChange={(e) => set(k, e.target.value)} data-testid={`app-field-${k}`} />
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="space-y-2">
          <p className="text-sm font-semibold text-slate-700">Grave / Deed Holder</p>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={deedSame} onCheckedChange={(v) => set('deed_holder_is_applicant', !!v)} data-testid="deed-same-checkbox" />
            Deed holder is the applicant ({draftData?.applicant_name || 'customer'})
          </label>
          {!deedSame && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Deed holder name</Label>
                <Input value={extra.deed_holder_name ?? ''} onChange={(e) => set('deed_holder_name', e.target.value)} data-testid="app-field-deed_holder_name" />
              </div>
              <div>
                <Label className="text-xs">Deed holder address</Label>
                <Input value={extra.deed_holder_address ?? ''} onChange={(e) => set('deed_holder_address', e.target.value)} data-testid="app-field-deed_holder_address" />
              </div>
            </div>
          )}
        </div>

        <div>
          <Label className="text-xs">Additional notes for the application</Label>
          <Textarea rows={2} value={extra.additional_notes ?? ''} onChange={(e) => set('additional_notes', e.target.value)} data-testid="app-field-additional_notes" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="app-details-save-btn">
            {saving ? 'Saving…' : 'Save Details'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
